import styled, { keyframes, css } from "styled-components";
import { tokens } from "@swiggy-private/dls-web-common/dist/components/tokens";

import { BILL_PAYMENT_SHIMMER_DIMENSIONS, TURBO_SHIMMER_DIMENSIONS } from "@dineout-cart-client/constants";

const SHIMMER_COLORS = {
    DARK_SUBTLE: "rgba(2, 6, 12, 0.05)",
    LIGHT_MID: "rgba(225, 225, 230, 0.45)",
} as const;

const gradientShimmer = keyframes`
    0% {
        background-position: -1000px 0;
    }

    100% {
        background-position: 1000px 0;
    }
`;

const headerShimmer = css`
    background: linear-gradient(
        90deg,
        ${tokens.colors.slateAlpha15} 0%,
        ${tokens.colors.slateAlpha05} 52.08%,
        ${tokens.colors.slateAlpha15} 100%
    );
    background-size: 1000px 100%;
    animation: ${gradientShimmer} 3s infinite linear;
`;

const blockShimmer = css`
    background: linear-gradient(
        90deg,
        ${SHIMMER_COLORS.DARK_SUBTLE} 0%,
        ${SHIMMER_COLORS.LIGHT_MID} 52.08%,
        ${SHIMMER_COLORS.DARK_SUBTLE} 100%
    );
    background-size: 1000px 100%;
    animation: ${gradientShimmer} 3s infinite linear;
`;

const turboShimmer = css`
    background: linear-gradient(
        90deg,
        ${(props) => props.theme.colors[props.theme.colorMode].background_Tertiary} 0%,
        ${(props) => props.theme.colors[props.theme.colorMode].background_Secondary} 52.08%,
        ${(props) => props.theme.colors[props.theme.colorMode].background_Tertiary} 100%
    );
    background-size: 1000px 100%;
    animation: ${gradientShimmer} 3s infinite linear;
`;

export const ShimmerContainer = styled.div(({ theme }) => ({
    width: "100%",
    minHeight: "100vh",
    backgroundColor: theme.colors[theme.colorMode].background_Secondary,
    display: "flex",
    flexDirection: "column",
}));

export const ShimmerHeader = styled.div`
    ${headerShimmer}
    width: 100%;
    min-height: ${BILL_PAYMENT_SHIMMER_DIMENSIONS.HEADER_HEIGHT}px;
    padding-top: var(--sat, 0px);
    padding-left: ${(props) => props.theme.spacing.medium};
    padding-right: ${(props) => props.theme.spacing.medium};
    padding-bottom: ${(props) => props.theme.spacing.large};
    border-radius: 0 0 ${(props) => props.theme.spacing.large} ${(props) => props.theme.spacing.large};
    position: relative;
`;

export const BackIconWrapper = styled.button(({ theme }) => ({
    position: "absolute",
    top: `calc(var(--sat, 0px) + ${theme.spacing.small})`,
    left: theme.spacing.small,
    zIndex: 1,
    padding: theme.spacing.small,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
}));

export const ShimmerContent = styled.div(({ theme }) => ({
    width: "100%",
    flex: 1,
    paddingTop: theme.spacing.x12,
    paddingLeft: theme.spacing.x10,
    paddingRight: theme.spacing.x10,
    paddingBottom: `calc(${theme.spacing.xLarge} * 6)`,
    backgroundColor: theme.colors[theme.colorMode].background_Secondary,
}));

export const ShimmerBlock = styled.div<{ $height: number }>`
    ${blockShimmer}
    height: ${(props) => props.$height}px;
    margin-bottom: ${(props) => props.theme.spacing.x12};
    border-radius: ${(props) => props.theme.cornerRadius.small};
`;

export const FixedBottomBlock = styled.div(({ theme }) => ({
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "100%",
    zIndex: 99,
    background: theme.colors[theme.colorMode].background_Primary,
    borderTopLeftRadius: theme.cornerRadius.small,
    borderTopRightRadius: theme.cornerRadius.small,
    boxShadow: theme.elevation[theme.colorMode].liftedNegative.boxShadow,
    paddingBottom: `calc(var(--sab, 0px) + ${theme.spacing.medium})`,
    "@supports (-webkit-touch-callout: none)": {
        "@supports (padding-bottom: env(safe-area-inset-bottom))": {
            paddingBottom: `calc(${theme.spacing.medium} + (env(safe-area-inset-bottom) / 2))`,
        },
    },
}));

export const BlockContent = styled.div(({ theme }) => ({
    background: theme.colors[theme.colorMode].background_Primary,
    padding: `${theme.spacing.medium} ${theme.spacing.medium} 0`,
    minHeight: `${TURBO_SHIMMER_DIMENSIONS.BLOCK_HEIGHT}px`,
}));

export const Line = styled.div<{ $width?: number; $height?: number }>`
    ${turboShimmer}
    height: ${(props) => props.$height || 12}px;
    width: ${(props) => (props.$width ? `${props.$width}%` : "100%")};
`;

export const PayButton = styled(Line)`
    border-radius: ${(props) => props.theme.cornerRadius.medium};
`;

export const PaymentMethodLine = styled(Line)`
    margin-bottom: ${(props) => props.theme.spacing.medium};
    border-radius: ${(props) => props.theme.cornerRadius.xxSmall};
`;

export const PaymentMethodIcon = styled(PaymentMethodLine)`
    margin-right: ${(props) => props.theme.spacing.xxSmall};
`;

export const Row = styled.div({
    display: "flex",
    alignItems: "center",
    flex: 1,
});

export const Col = styled.div({
    display: "flex",
    flexDirection: "column",
    flex: 1,
});
